import { GatedQueryError } from "../GatedQueryError";
import {
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Checkbox,
  DataTable,
  InlineStack,
  Pagination,
  Select,
  Text,
  TextField,
} from "@shopify/polaris";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  UNASSIGNED_PAGE_LIMIT,
  assignVariantSupplier,
  assignVariantsSupplier,
  listSuppliers,
  listUnassignedVariants,
} from "../../api/purchase_orders";
import {
  canonicalStockyPublicBigIntId,
  isStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "../../api/stocky_public_ids";
import {
  AddSupplierModal,
  NEW_SUPPLIER_OPTION_VALUE,
} from "./AddSupplierModal";

import { t } from "../../lib/i18n";
import { displayVariantTitle } from "../../utils/productTitles";

const SEARCH_DEBOUNCE_MS = 300;
const REORDER_POLICIES_PATH = "/reorder-policies";

function unassignedVariantTitle(variant: {
  display_title?: string | null;
  product_title?: string | null;
  title: string | null;
}): string {
  const displayed = variant.display_title?.trim() ?? "";
  if (displayed) return displayed;
  return (
    displayVariantTitle(variant.product_title, variant.title) ??
    t("(名称未設定)")
  );
}

interface SupplierSetupCardProps {
  /** 注文書ドラフトが1件でもあるか（空表の理由を出し分けるため）。 */
  hasDrafts: boolean;
}

/**
 * 注文書を出せる状態にするための残り作業だけを出し、終わったら消えるカード。
 * 「表がなぜ空か」の説明はこのコンポーネントが一手に持つ（呼び出し側と二重に
 * メッセージを出して矛盾させないため）。
 *
 * 背景: 注文書は仕入先ごとに束ねるため `Variant.supplier_id` が無い商品はドラフトに
 * 現れない（`build_purchase_order_drafts` が WHERE で除外）。ところが仕入先を
 * 割り当てる UI はそのドラフト表の中の「サプライヤー変更」しか無く、Stocky 移行も
 * `supplier_id` を書かない。つまり「割り当てないと割当UIが出てこない」閉ループで、
 * 新規インストールの店は注文書が永久に空だった。ここがその外側の入口になる。
 *
 * プランゲートは掛けない: Free のウェッジである手動注文書も割当済み商品が前提の
 * ため（backend の `POST /suppliers` / `GET /unassigned-variants` も同じ理由で非ゲート）。
 */
export function SupplierSetupCard({ hasDrafts }: SupplierSetupCardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<StockyPublicBigIntId[]>([]);
  const [bulkSupplierId, setBulkSupplierId] = useState("");

  // 検索入力はデバウンスしてから問い合わせる（1打鍵1リクエストにしない）。
  useEffect(() => {
    const id = setTimeout(() => {
      setQuery(searchInput);
      setOffset(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchInput]);

  // 表示中の行が入れ替わったら選択を捨てる。見えていない商品を一括で書き換えて
  // しまわないため（ページ送り・検索の前の選択が残ると事故になる）。
  useEffect(() => {
    setSelectedIds([]);
  }, [query, offset]);

  const suppliersQuery = useQuery({
    queryKey: ["po-suppliers"],
    queryFn: listSuppliers,
  });
  const unassignedQuery = useQuery({
    queryKey: ["po-unassigned", query, offset],
    queryFn: () => listUnassignedVariants(query, UNASSIGNED_PAGE_LIMIT, offset),
    placeholderData: keepPreviousData,
  });

  const assignMutation = useMutation({
    mutationFn: (vars: {
      variantId: StockyPublicBigIntId;
      supplierId: StockyPublicBigIntId;
    }) => assignVariantSupplier(vars.variantId, vars.supplierId),
    onSuccess: () => {
      // 割り当てた商品は未割当一覧から消え、ドラフト側に現れる。
      queryClient.invalidateQueries({ queryKey: ["po-unassigned"] });
      queryClient.invalidateQueries({ queryKey: ["purchase-order-drafts"] });
    },
  });

  const bulkAssignMutation = useMutation({
    mutationFn: (vars: {
      variantIds: StockyPublicBigIntId[];
      supplierId: StockyPublicBigIntId;
    }) => assignVariantsSupplier(vars.variantIds, vars.supplierId),
    onSuccess: () => {
      setSelectedIds([]);
      setBulkSupplierId("");
      queryClient.invalidateQueries({ queryKey: ["po-unassigned"] });
      queryClient.invalidateQueries({ queryKey: ["purchase-order-drafts"] });
    },
  });

  const suppliers = suppliersQuery.data ?? [];
  const hasSuppliers = suppliers.length > 0;
  const variants = unassignedQuery.data?.variants ?? [];
  const total = unassignedQuery.data?.total ?? 0;
  const isSearching = query.trim() !== "";

  const supplierOptions = suppliers.map((s) => ({
    label: s.name,
    value: String(s.id),
  }));
  // 末尾に「＋ 新規登録」を混ぜる＝割り当てたい仕入先がまだ無いと気づいた瞬間に
  // その場から登録へ飛べる（2026-08-03 実機踏破の要望）。sentinel は割当に流さない。
  const assignSelectOptions = [
    ...supplierOptions,
    { label: t("＋ 新規仕入先を登録…"), value: NEW_SUPPLIER_OPTION_VALUE },
  ];
  const goToNewSupplier = () => navigate("/suppliers?add=1");
  const isAssigning = assignMutation.isPending || bulkAssignMutation.isPending;
  const selectedCount = selectedIds.length;
  const isAllOnPageSelected =
    variants.length > 0 && selectedCount === variants.length;

  const toggleOne = (variantId: StockyPublicBigIntId) => {
    const canonicalId = canonicalStockyPublicBigIntId(variantId);
    setSelectedIds((ids) =>
      ids.some((id) => canonicalStockyPublicBigIntId(id) === canonicalId)
        ? ids.filter((id) => canonicalStockyPublicBigIntId(id) !== canonicalId)
        : [...ids, canonicalId],
    );
  };
  const toggleAllOnPage = () =>
    setSelectedIds(
      isAllOnPageSelected
        ? []
        : variants.map((variant) =>
            canonicalStockyPublicBigIntId(variant.variant_id),
          ),
    );

  if (suppliersQuery.isError || unassignedQuery.isError) {
    return (
      <GatedQueryError
        title={t("セットアップ状況の取得に失敗しました")}
        onRetry={() => {
          void suppliersQuery.refetch();
          void unassignedQuery.refetch();
        }}
      />
    );
  }

  // 初回ロード中は何も出さない（設定済みの店で一瞬カードが閃くのを避ける）。
  if (suppliersQuery.isLoading || unassignedQuery.isLoading) return null;

  // 仕入先があり未割当もゼロ＝セットアップ完了。大きなカードは畳むが、
  // 🔴「仕入先を追加」の入口は殺さない＝2026-08-03 の実機踏破で、vendor 自動割当が
  // 全商品に効いた店では新規仕入先を登録する経路が UI から消えていた（早期 return が
  // 追加ボタンごとカードを消していた）。完了状態でも控えめな入口を常設する。
  const isSetupDone = hasSuppliers && total === 0 && !isSearching;

  const hasNextPage = offset + UNASSIGNED_PAGE_LIMIT < total;
  const hasPreviousPage = offset > 0;

  return (
    <>
      {isSetupDone ? (
        hasDrafts ? (
          <InlineStack align="end">
            <Button variant="plain" onClick={() => setIsModalOpen(true)}>
              {t("仕入先を追加")}
            </Button>
          </InlineStack>
        ) : (
          // 🔴 「仕入先も割当も終わっているのに表が空」＝残る理由は①在庫がまだ発注点を
          // 割っていない ②発注点を設定していない、の2つ。行き先ボタンを添える
          // （2026-07-30 に実際に手が止まった）。
          <Banner
            tone="info"
            title={t("発注が必要な商品はありません")}
            action={{
              content: t("発注点を設定する"),
              onAction: () => navigate(REORDER_POLICIES_PATH),
            }}
            secondaryAction={{
              content: t("仕入先を追加"),
              onAction: () => setIsModalOpen(true),
            }}
          >
            <p>
              {t(
                "在庫が発注点を割った商品があると、ここに注文書ドラフトが自動で作られます。まだ発注点を設定していない場合は、先に仕入先ごとのリードタイムと安全在庫日数を入力してください。",
              )}
            </p>
          </Banner>
        )
      ) : (
        <Card>
          <BlockStack gap="400">
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">
                {hasSuppliers
                  ? t("仕入先が未割当の商品が{n}件あります", { n: total })
                  : t("仕入先が未登録です")}
              </Text>
              <Text as="p" tone="subdued">
                {hasSuppliers
                  ? t(
                      "商品に仕入先を割り当てると、注文書ドラフトに表示されます。発注しない商品は割り当てなくて構いません。",
                    )
                  : t(
                      "注文書は仕入先ごとにまとめます。仕入先を登録し、商品を割り当てると注文書ドラフトが作られます。",
                    )}
              </Text>
            </BlockStack>

            <InlineStack gap="300" blockAlign="center">
              <Button
                variant={hasSuppliers ? "secondary" : "primary"}
                onClick={() => setIsModalOpen(true)}
              >
                {t("仕入先を追加")}
              </Button>
              {hasSuppliers && (
                <Text as="span" tone="subdued">
                  {t("登録済み {n} 件", { n: suppliers.length })}
                </Text>
              )}
            </InlineStack>

            {(assignMutation.isError || bulkAssignMutation.isError) && (
              <Banner
                tone="critical"
                title={t("仕入先の割り当てに失敗しました")}
              >
                <p>{t("時間をおいて再度お試しください。")}</p>
              </Banner>
            )}

            {hasSuppliers && (
              <BlockStack gap="300">
                <TextField
                  label={t("商品を検索")}
                  value={searchInput}
                  onChange={setSearchInput}
                  placeholder={t("商品名 / SKU")}
                  autoComplete="off"
                  clearButton
                  onClearButtonClick={() => setSearchInput("")}
                />

                {variants.length === 0 ? (
                  <Text as="p" tone="subdued">
                    {t("該当する未割当の商品はありません。")}
                  </Text>
                ) : (
                  <>
                    {selectedCount > 0 && (
                      <InlineStack gap="300" blockAlign="center">
                        <Text as="span" fontWeight="semibold">
                          {t("{n}件を選択中", { n: selectedCount })}
                        </Text>
                        <Box minWidth="200px">
                          <Select
                            label={t("一括で割り当てる仕入先")}
                            labelHidden
                            placeholder={t("仕入先を選択")}
                            options={assignSelectOptions}
                            value={bulkSupplierId}
                            disabled={isAssigning}
                            onChange={(value) =>
                              value === NEW_SUPPLIER_OPTION_VALUE
                                ? goToNewSupplier()
                                : setBulkSupplierId(value)
                            }
                          />
                        </Box>
                        <Button
                          variant="primary"
                          disabled={bulkSupplierId === "" || isAssigning}
                          loading={bulkAssignMutation.isPending}
                          onClick={() => {
                            if (!isStockyPublicBigIntId(bulkSupplierId)) return;
                            bulkAssignMutation.mutate({
                              variantIds: selectedIds,
                              supplierId:
                                canonicalStockyPublicBigIntId(bulkSupplierId),
                            });
                          }}
                        >
                          {t("まとめて割り当て")}
                        </Button>
                        <Button
                          variant="plain"
                          onClick={() => setSelectedIds([])}
                        >
                          {t("選択を解除")}
                        </Button>
                      </InlineStack>
                    )}

                    <DataTable
                      columnContentTypes={["text", "text", "text", "text"]}
                      headings={[
                        <Checkbox
                          key="select-all"
                          label={t("このページの商品をすべて選択")}
                          labelHidden
                          checked={isAllOnPageSelected}
                          disabled={isAssigning}
                          onChange={toggleAllOnPage}
                        />,
                        t("商品名"),
                        "SKU",
                        t("仕入先"),
                      ]}
                      rows={variants.map((v) => [
                        <Checkbox
                          key={`select-${v.variant_id}`}
                          label={`${unassignedVariantTitle(v)}を選択`}
                          labelHidden
                          checked={selectedIds.some(
                            (id) =>
                              canonicalStockyPublicBigIntId(id) ===
                              canonicalStockyPublicBigIntId(v.variant_id),
                          )}
                          disabled={isAssigning}
                          onChange={() => toggleOne(v.variant_id)}
                        />,
                        unassignedVariantTitle(v),
                        v.sku ?? "—",
                        <Box minWidth="200px" key={`assign-${v.variant_id}`}>
                          <Select
                            label={t("仕入先")}
                            labelHidden
                            placeholder={t("選択してください")}
                            options={assignSelectOptions}
                            value=""
                            disabled={isAssigning}
                            onChange={(value) => {
                              if (value === NEW_SUPPLIER_OPTION_VALUE) {
                                goToNewSupplier();
                                return;
                              }
                              if (!isStockyPublicBigIntId(value)) return;
                              assignMutation.mutate({
                                variantId: v.variant_id,
                                supplierId:
                                  canonicalStockyPublicBigIntId(value),
                              });
                            }}
                          />
                        </Box>,
                      ])}
                    />
                  </>
                )}

                {(hasNextPage || hasPreviousPage) && (
                  <InlineStack align="center">
                    <Pagination
                      label={`${offset + 1}–${Math.min(
                        offset + variants.length,
                        total,
                      )} / ${total}`}
                      hasPrevious={hasPreviousPage}
                      onPrevious={() =>
                        setOffset((o) => Math.max(0, o - UNASSIGNED_PAGE_LIMIT))
                      }
                      hasNext={hasNextPage}
                      onNext={() => setOffset((o) => o + UNASSIGNED_PAGE_LIMIT)}
                    />
                  </InlineStack>
                )}
              </BlockStack>
            )}
          </BlockStack>
        </Card>
      )}

      <AddSupplierModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}
